import * as React from 'react';
import { Button, makeStyles, tokens, Text } from '@fluentui/react-components';
import {
  CheckmarkCircle16Filled,
  ChevronRight16Regular,
  DismissCircle16Regular,
} from '@fluentui/react-icons';
import { DocumentRisk, RiskType } from '../../services/analysis';

export type RiskStatus = 'active' | 'applied' | 'dismissed';

interface RiskCardProps {
  risk: DocumentRisk;
  expanded: boolean;
  status: RiskStatus;
  onToggle: () => void;
  onApply: (rewrite: string) => void;
  onDismiss: () => void;
}

interface TypeStyle {
  bg: string;
  fg: string;
  label: string;
}

const TYPE_STYLES: Record<RiskType, TypeStyle> = {
  legal: { bg: '#c50f1f', fg: '#ffffff', label: 'Legal Risk' },
  business: { bg: '#ca5010', fg: '#ffffff', label: 'Business Risk' },
  structural: { bg: '#5c2e91', fg: '#ffffff', label: 'Structural Risk' },
};

const useStyles = makeStyles({
  card: {
    position: 'relative',
    padding: '12px',
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    marginBottom: '8px',
    cursor: 'pointer',
    transition: 'border-color 0.12s ease, background-color 0.12s ease, opacity 0.12s ease',
  },
  cardSelected: {
    border: `1px solid ${tokens.colorBrandStroke1}`,
    boxShadow: `0 0 0 1px ${tokens.colorBrandStroke1}`,
  },
  cardApplied: {
    backgroundColor: tokens.colorNeutralBackground3,
    opacity: 0.85,
    cursor: 'default',
  },
  cardDismissed: {
    backgroundColor: tokens.colorNeutralBackground2,
    opacity: 0.55,
    cursor: 'default',
  },
  badgeRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '8px',
  },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '2px 8px',
    borderRadius: '10px',
    fontSize: tokens.fontSizeBase100,
    fontWeight: tokens.fontWeightSemibold,
    letterSpacing: '0.2px',
  },
  badgeMuted: {
    opacity: 0.7,
  },
  rightStack: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  appliedIcon: {
    color: '#107c10', // Fluent green
  },
  dismissedIcon: {
    color: tokens.colorNeutralForeground3,
  },
  statusLabel: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
  },
  chevron: {
    color: tokens.colorNeutralForeground3,
    transition: 'transform 0.12s ease',
  },
  chevronOpen: {
    transform: 'rotate(90deg)',
  },
  title: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    marginBottom: '6px',
  },
  description: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
    lineHeight: tokens.lineHeightBase200,
  },
  snippetBox: {
    marginTop: '10px',
    padding: '8px',
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusSmall,
    fontSize: tokens.fontSizeBase200,
    fontStyle: 'italic',
    color: tokens.colorNeutralForeground2,
    whiteSpace: 'pre-wrap',
  },
  altLabel: {
    marginTop: '10px',
    marginBottom: '4px',
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground2,
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
  },
  alternative: {
    padding: '8px',
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusSmall,
    fontSize: tokens.fontSizeBase300,
    whiteSpace: 'pre-wrap',
    marginBottom: '8px',
  },
  actionsRow: {
    display: 'flex',
    gap: '6px',
    flexWrap: 'wrap',
  },
  noFix: {
    marginTop: '10px',
    fontSize: tokens.fontSizeBase200,
    fontStyle: 'italic',
    color: tokens.colorNeutralForeground3,
  },
});

const RiskCard: React.FC<RiskCardProps> = ({
  risk,
  expanded,
  status,
  onToggle,
  onApply,
  onDismiss,
}) => {
  const styles = useStyles();
  const ts = TYPE_STYLES[risk.type];
  const inactive = status !== 'active';
  const showExpanded = expanded && status === 'active';

  const cardClass = `${styles.card} ${
    showExpanded ? styles.cardSelected : ''
  } ${status === 'applied' ? styles.cardApplied : ''} ${
    status === 'dismissed' ? styles.cardDismissed : ''
  }`;

  return (
    <div
      className={cardClass}
      onClick={() => {
        if (!inactive) onToggle();
      }}
      role="button"
      tabIndex={inactive ? -1 : 0}
      aria-disabled={inactive}
      onKeyDown={e => {
        if (inactive) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onToggle();
        }
      }}
    >
      <div className={styles.badgeRow}>
        <span
          className={`${styles.badge} ${inactive ? styles.badgeMuted : ''}`}
          style={{ backgroundColor: ts.bg, color: ts.fg }}
        >
          {ts.label}
        </span>
        <div className={styles.rightStack}>
          {status === 'applied' && (
            <>
              <span className={styles.statusLabel}>Applied</span>
              <CheckmarkCircle16Filled className={styles.appliedIcon} aria-label="Applied" />
            </>
          )}
          {status === 'dismissed' && (
            <>
              <span className={styles.statusLabel}>Dismissed</span>
              <DismissCircle16Regular className={styles.dismissedIcon} aria-label="Dismissed" />
            </>
          )}
          {status === 'active' && (
            <ChevronRight16Regular
              className={`${styles.chevron} ${showExpanded ? styles.chevronOpen : ''}`}
            />
          )}
        </div>
      </div>
      <div className={styles.title}>{risk.title}</div>
      <div className={styles.description}>{risk.description}</div>

      {showExpanded && (
        <>
          {risk.snippet && <div className={styles.snippetBox}>”{risk.snippet}”</div>}
          {risk.suggestedAlternative ? (
            <>
              <div className={styles.altLabel}>Suggested alternative</div>
              <div className={styles.alternative}>{risk.suggestedAlternative}</div>
              <div className={styles.actionsRow}>
                <Button
                  size="small"
                  appearance="primary"
                  onClick={e => {
                    e.stopPropagation();
                    onApply(risk.suggestedAlternative!);
                  }}
                >
                  Apply alternative
                </Button>
                <Button
                  size="small"
                  appearance="secondary"
                  onClick={e => {
                    e.stopPropagation();
                    onDismiss();
                  }}
                >
                  Dismiss
                </Button>
              </div>
            </>
          ) : (
            <>
              <Text className={styles.noFix}>
                No clause-level rewrite — review and address inline.
              </Text>
              <div className={styles.actionsRow} style={{ marginTop: '8px' }}>
                <Button
                  size="small"
                  appearance="secondary"
                  onClick={e => {
                    e.stopPropagation();
                    onDismiss();
                  }}
                >
                  Dismiss
                </Button>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default RiskCard;
